import { useNavigate } from "react-router-dom";
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Box,
  Typography,
  Button,
  IconButton,
} from "@mui/material";
import { useEffect, useState } from "react";
import SearchBar from "../../components/SearchBar";
import AddIcon from "@mui/icons-material/Add";
import { isValidInput } from "../../utils/validations";
import { showError, showToast } from "../../constants/toasts";
import {
  useDeleteRemainderMutation,
  useLazyGetRemainderQuery,
} from "../../services/reminder";
import usePermission from "../../hooks/usePermission";

const ReminderEngine = () => {
  const navigate = useNavigate();

  // Permission hook for Reminder Engine module
  const { 
    hasPermission, 
    isLoading: permissionLoading, 
    canView, 
    canAddEdit, 
    canDelete 
  } = usePermission({
    module: "Reminder Engine",
    permission_type: "can_view"
  });

  const FEATURE_LABELS: Record<string, string> = {
    SMART_HYDRATION_NUDGES: "Smart Hydration Nudges",
    CALENDAR_SYNC_WINDOW_BEHAVIOR: "Calendar Sync Window Behavior",
    WEATHER_BASED_HYDRATION_ALGORITHM: "Weather-Based Hydration Algorithm",
  };

  const WEATHER_LABELS: Record<string, string> = {
    SUMMER: "Summer",
    WINTER: "Winter",
    SPRING: "Spring",
    AUTUMN: "Autumn",
  };

  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<any[]>([]);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState("");

  const [getReminder] = useLazyGetRemainderQuery();
  const [deleteReminder] = useDeleteRemainderMutation();

  const getReminderList = async () => {
    setIsLoading(true);
    try {
      const response = await getReminder({
        search: debouncedSearchTerm,
      }).unwrap();
      if (response?.status === 200) {
        setData(response?.data || []);
      } else {
        setData([]);
      }
    } catch (error) {
      setData([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getReminderList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm]);

  const handleDelete = async (id: number) => {
    if (!id) return;
    const confirmed = window.confirm(
      "Are you sure you want to delete this record?"
    );
    if (!confirmed) return;
    try {
      await deleteReminder({ id }).unwrap();
      showToast("Deleted successfully");
      getReminderList();
    } catch (e: any) {
      showError(e?.data?.message || "Failed to delete");
    }
  };

  // Show loading while checking permissions
  if (permissionLoading) {
    return <div>Loading permissions...</div>;
  }

  // Show access denied if user doesn't have view permission
  if (!hasPermission) {
    return <div>Access Denied</div>;
  }

  return (
    <>
      <div className="main_title">
        <h1>Reminder Engine</h1>
        <p>
          <span onClick={() => navigate("/dashboard")}>Dashboard</span> -
          Reminder Engine
        </p>
      </div>

      <div className="cards">
        <div className="table_header">
          <div className="left_s">
            <Box className="search_box">
              <SearchBar
                placeholder="Search..."
                searchTerm={searchTerm}
                setDebouncedSearchTerm={setDebouncedSearchTerm}
                value={searchTerm}
                onCross={() => setSearchTerm("")}
                onChange={(val: any) => {
                  if (isValidInput(val.target.value)) {
                    setSearchTerm(val.target.value);
                  }
                }}
              />
            </Box>
          </div>
          {canAddEdit("Reminder Engine") && (
            <div className="right_s">
              <Button
                className="btn btn_primary"
                onClick={() => navigate("/reminder-engine/add")}
              >
                <AddIcon /> Add{" "}
              </Button>
            </div>
          )}
        </div>
        <TableContainer className="table_container">
          <Table>
            <TableHead>
              <TableRow hover>
                <TableCell>Feature Name</TableCell>
                <TableCell>Message</TableCell>
                <TableCell>Weather</TableCell>
                {(canAddEdit("Reminder Engine") || canDelete("Reminder Engine")) && (
                  <TableCell>Action</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((row) => (
                <TableRow hover key={row.id}>
                  <TableCell>
                    <Typography fontWeight="bold">
                      {FEATURE_LABELS[row?.Feature_name as string] || row?.Feature_name || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography>{row?.message || "-"}</Typography>
                  </TableCell>
                  <TableCell>
                    <b>{WEATHER_LABELS[row?.weather as string] || row?.weather || "-"}</b>
                  </TableCell>
                  {(canAddEdit("Reminder Engine") || canDelete("Reminder Engine")) && (
                    <TableCell>
                      <div className="table_actions v2">
                        {canAddEdit("Reminder Engine") && (
                          <Box>
                            <IconButton
                              onClick={() =>
                                navigate(`/reminder-engine/add?id=${row.id}`)
                              }
                            >
                              <img src="/static/images/edit2_icon.svg" alt="Edit" />
                            </IconButton>
                          </Box>
                        )}
                        {canDelete("Reminder Engine") && (
                          <Box>
                            <IconButton onClick={() => handleDelete(row.id)}>
                              <img
                                src="/static/images/trash2_icon.svg"
                                alt="Delete"
                              />
                            </IconButton>
                          </Box>
                        )}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </>
  );
};

export default ReminderEngine;
